<template>
  <div
    class="fixed inset-0 overflow-y-auto z-50 bg-gray-500/75 transition-opacity"
  >
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <MLoader v-if="isLoading" />
      <div
        v-else
        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full"
      >
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <div class="flex justify-between items-center">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  {{ title }}
                </h3>
                <button
                  @click="$emit('toggle-edit')"
                  class="text-sm font-medium text-brand-700 hover:text-brand-800"
                  v-if="canEdit"
                >
                  {{ editMode ? "Cancel" : "Edit" }}
                </button>
              </div>

              <HistoryPath
                v-if="!editMode && !isCreateMode && rfi"
                :statusHistory="rfi.statusHistory"
                :reassignedByHistory="rfi.reassignedByHistory"
                :initiallyExpanded="false"
              />

              <AttachmentList
                v-if="!editMode && !isCreateMode && rfi"
                :attachments="rfi.attachments"
                :initiallyExpanded="true"
              />

              <div class="mt-4 grid grid-cols-1 gap-y-4 gap-x-8 sm:grid-cols-2">
                <template v-for="field in editableFields" :key="field.key">
                <div v-if="shouldShowImpactField(field.key)">
                  <p class="text-sm font-medium text-gray-500">
                    {{ field.label }}
                  </p>
                  <template v-if="editMode || isCreateMode">
                    <!-- Input para prioridad (select) -->
                    <select
                      v-if="field.key === 'priority'"
                      :value="editedRfi[field.key]"
                      @change="
                        $emit('update-field', {
                          field: field.key,
                          value: $event.target.value,
                        })
                      "
                      class="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm rounded-md"
                    >
                      <option
                        v-for="priority in priorities"
                        :key="priority"
                        :value="priority"
                      >
                        {{ priority }}
                      </option>
                    </select>

                    <select
                      v-else-if="field.key === 'discipline_classification'"
                      :value="editedRfi[field.key]"
                      @change="
                        $emit('update-field', {
                          field: field.key,
                          value: $event.target.value,
                        })
                      "
                      class="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm rounded-md"
                    >
                      <option value="">Select Discipline</option>
                      <option
                        v-for="discipline in disciplineOptions"
                        :key="discipline"
                        :value="discipline"
                      >
                        {{ discipline }}
                      </option>
                    </select>

                    <input
                      v-else-if="field.key === 'company'"
                      :value="editedRfi[field.key]"
                      readonly
                      type="text"
                      class="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm bg-gray-100"
                    />

                    <input
                      v-else-if="field.key === 'requestor_name'"
                      :value="editedRfi[field.key]"
                      readonly
                      type="text"
                      class="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm bg-gray-100"
                    />

                    <input
                      v-else-if="field.key === 'date_initiated'"
                      :value="today"
                      readonly
                      type="date"
                      class="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm bg-gray-100"
                    />

                    <!-- <input
                      v-else-if="
                        field.key === 'directed_to_email' &&
                        canEditDirectedToEmail
                      "
                      :value="editedRfi[field.key]"
                      @input="handleFieldUpdate(field.key, $event.target.value)"
                      type="email"
                      placeholder="Enter recipient email soy reasigned"
                      :required="isReassignedStatus"
                      :class="[
                        'mt-1 block w-full pl-3 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm',
                        isReassignedStatus && !editedRfi[field.key]
                          ? 'border-accent-500 bg-accent-50'
                          : 'border-gray-300',
                      ]"
                    /> -->
                    <div
                      v-else-if="
                        field.key === 'directed_to_email' && field.editable
                      "
                    >
                      <input
                        :value="editedRfi[field.key]"
                        @input="
                          handleFieldUpdate(field.key, $event.target.value)
                        "
                        type="email"
                        placeholder="Enter recipient email"
                        :class="[
                          'mt-1 block w-full pl-3 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm',
                          isReassignedStatus && !editedRfi[field.key]
                            ? 'border-accent-500 bg-accent-50'
                            : 'border-gray-300',
                        ]"
                      />
                      <div
                        v-if="validationErrors[field.key]"
                        class="text-red-500 text-xs mt-1"
                      >
                        {{ validationErrors[field.key] }}
                      </div>
                    </div>
                    <div
                      v-else-if="
                        field.key === 'reassigned_by' && field.editable
                      "
                    >
                      <input
                        :value="editedRfi[field.key]"
                        @input="
                          handleFieldUpdate(field.key, $event.target.value)
                        "
                        type="email"
                        placeholder="Enter  email"
                        :class="[
                          'mt-1 block w-full pl-3 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm',
                          isReassignedStatus && !editedRfi[field.key]
                            ? 'border-accent-500 bg-accent-50'
                            : 'border-gray-300',
                        ]"
                      />
                      <div
                        v-if="validationErrors[field.key]"
                        class="text-red-500 text-xs mt-1"
                      >
                        {{ validationErrors[field.key] }}
                      </div>
                    </div>
                    <select
                      v-else-if="field.key === 'directed_to' && isCreateMode"
                      :value="editedRfi[field.key]"
                      @change="
                        handleFieldUpdate(field.key, $event.target.value)
                      "
                      :required="isReassignedStatus"
                      :class="[
                        'mt-1 block w-full pl-3 pr-10 py-2 text-base border rounded-md focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm',
                        isReassignedStatus && !editedRfi[field.key]
                          ? 'border-accent-500 bg-accent-50'
                          : 'border-gray-300',
                      ]"
                    >
                      <option value="" disabled>Select Recipient</option>
                      <option
                        v-for="option in directedOptions"
                        :key="option"
                        :value="option"
                      >
                        {{ option }}
                      </option>
                    </select>

                    <select
                      v-else-if="
                        field.key === 'directed_to' && canEditDirectedTo
                      "
                      :value="editedRfi[field.key]"
                      @change="
                        handleFieldUpdate(field.key, $event.target.value)
                      "
                      :required="isReassignedStatus"
                      :class="[
                        'mt-1 block w-full pl-3 pr-10 py-2 text-base border rounded-md focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm',
                        isReassignedStatus && !editedRfi[field.key]
                          ? 'border-accent-500 bg-accent-50'
                          : 'border-gray-300',
                      ]"
                    >
                      <option value="">Select Recipient</option>
                      <option
                        v-for="option in directedOptions"
                        :key="option"
                        :value="option"
                      >
                        {{ option }}
                      </option>
                    </select>

                    <input
                      v-else-if="field.key === 'directed_to' && field.editable"
                      :value="editedRfi[field.key]"
                      @input="handleFieldUpdate(field.key, $event.target.value)"
                      type="text"
                      placeholder="Enter recipient name"
                      class="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                    />
                    <div v-else-if="field.key === 'status'">
                      <select
                        :value="editedRfi[field.key]"
                        required
                        @change="
                          $emit('update-field', {
                            field: field.key,
                            value: $event.target.value,
                          })
                        "
                        class="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm rounded-md"
                      >
                        <option
                          v-if="isCreateMode"
                          :value="'Submitted and waiting'"
                        >
                          Submitted and waiting
                        </option>
                        <option
                          v-else
                          v-for="status in statusOptions"
                          :key="status"
                          :value="status"
                          :selected="status === editedRfi[field.key]"
                        >
                          {{ status }}
                        </option>
                      </select>
                      <div
                        v-if="validationErrors.status"
                        class="text-red-500 text-xs mt-1"
                      >
                        {{ validationErrors.status }}
                      </div>
                    </div>

                    <textarea
                      v-else-if="['question_request'].includes(field.key)"
                      :value="editedRfi[field.key]"
                      @input="
                        $emit('update-field', {
                          field: field.key,
                          value: $event.target.value,
                        })
                      "
                      rows="3"
                      class="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                    ></textarea>

                    <div v-else-if="['possible_impact'].includes(field.key)">
                      <label
                        >**Hold Ctrl (Cmd on Mac) to select multiple
                        options</label
                      >
                      <select
                        v-model="selectedImpacts"
                        @change="handleMultiSelectChange(field.key)"
                        multiple
                        size="4"
                        class="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                      >
                        <option value="Cost Impact">Cost Impact</option>
                        <option value="Duration Impact">Duration Impact</option>
                        <option value="Possible Hazard">Possible Hazard</option>
                        <option value="Weather Impact">Weather Impact</option>
                        <option value="Contract Conflict">
                          Contract Conflict
                        </option>
                        <option value="Faulty Tools">Faulty Tools</option>
                        <option value="Faulty Equipment">
                          Faulty Equipment
                        </option>
                        <option value="Missing Documentation">
                          Missing Documentation
                        </option>
                        <option value="Coordination Problem">
                          Coordination Problem
                        </option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div
                      v-else-if="
                        field.key === 'requestor_email' && field.editable
                      "
                    >
                      <input
                        :value="editedRfi[field.key]"
                        @input="
                          handleFieldUpdate(field.key, $event.target.value)
                        "
                        readonly
                        type="email"
                        placeholder="Enter requestor email"
                        :class="[
                          'mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm bg-gray-100',
                          isReassignedStatus && !editedRfi[field.key]
                            ? 'border-accent-500 bg-accent-50'
                            : 'border-gray-300',
                        ]"
                      />
                      <div
                        v-if="validationErrors[field.key]"
                        class="text-red-500 text-xs mt-1"
                      >
                        {{ validationErrors[field.key] }}
                      </div>
                    </div>

                    <div
                      v-else-if="
                        field.key === 'manager_email' && field.editable
                      "
                    >
                      <input
                        :value="editedRfi[field.key]"
                        @input="
                          handleFieldUpdate(field.key, $event.target.value)
                        "
                        type="email"
                        placeholder="Enter manager email"
                        :class="[
                          'mt-1 block w-full pl-3 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm',
                          isReassignedStatus && !editedRfi[field.key]
                            ? 'border-accent-500 bg-accent-50'
                            : 'border-gray-300',
                        ]"
                      />
                      <div
                        v-if="validationErrors[field.key]"
                        class="text-red-500 text-xs mt-1"
                      >
                        {{ validationErrors[field.key] }}
                      </div>
                    </div>
                    <div v-else>
                      <input
                        :value="editedRfi[field.key]"
                        @input="
                          $emit('update-field', {
                            field: field.key,
                            value: $event.target.value,
                          })
                        "
                        type="text"
                        class="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                      />
                      <div
                        v-if="validationErrors[field.key]"
                        class="text-red-500 text-xs mt-1"
                      >
                        {{ validationErrors[field.key] }}
                      </div>
                    </div>
                  </template>

                  <template v-else>
                    <template v-if="rfi">
                      <template v-if="field.key === 'priority'">
                        <span
                          :class="priorityClasses(rfi.priority)"
                          class="px-2 py-1 rounded-full text-xs"
                        >
                          {{ rfi.priority }}
                        </span>
                      </template>
                      <template v-else-if="field.key === 'date_initiated'">
                        <p class="mt-1 text-sm text-gray-900">
                          {{ formatDate(rfi.date_initiated) }}
                        </p>
                      </template>
                      <template v-else-if="field.key === 'directed_to_email'">
                        <p class="mt-1 text-sm text-gray-900">
                          {{ rfi[field.key] || "N/A" }}
                          <span
                            v-if="canReassign"
                            class="ml-2 text-xs text-brand-600"
                          >
                            (You can reassign)
                          </span>
                        </p>
                      </template>
                      <template v-else-if="field.key === 'possible_impact'">
                        <p
                          class="mt-1 text-sm text-gray-900 whitespace-pre-line"
                        >
                          {{
                            Array.isArray(rfi[field.key])
                              ? rfi[field.key].join(", ")
                              : rfi[field.key] || "N/A"
                          }}
                        </p>
                      </template>
                      <template v-else>
                        <p
                          class="mt-1 text-sm text-gray-900 whitespace-pre-line"
                        >
                          {{ rfi[field.key] || "N/A" }}
                        </p>
                      </template>
                    </template>
                    <template v-else>
                      <p class="mt-1 text-sm text-gray-900">N/A</p>
                    </template>
                  </template>
                </div>
                </template>
              </div>
            </div>
          </div>
        </div>
        <FileAttachments v-if="isCreateMode" v-model="attachments" />
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            v-if="editMode || isCreateMode"
            type="button"
            @click="handleSubmit"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-accent-gradient text-base font-medium text-white hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 sm:ml-3 sm:w-auto sm:text-sm"
            :disabled="saving"
          >
            <span v-if="saving">Saving...</span>
            <span v-else>{{ isCreateMode ? "Create" : "Save Changes" }}</span>
          </button>
          <button
            type="button"
            @click="$emit('close')"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import HistoryPath from "@/components/rfi-management/RFIHistoryPath.vue";
import AttachmentList from "@/components/rfi-management/RFIAttachments.vue";
import MLoader from "@/components/MainLoader.vue";
import FileAttachments from "@/components/FileAttachments.vue";

export default {
  props: {
    rfi: Object,
    editMode: Boolean,
    editedRfi: Object,
    saving: Boolean,
    priorities: Array,
    disciplines: Array,
    isCreateMode: {
      type: Boolean,
      default: false,
    },
    editableFields: Object,
    isLoading: Boolean,
  },
  emits: ["toggle-edit", "save", "close", "update-field"],
  components: { HistoryPath, AttachmentList, MLoader, FileAttachments },
  setup(props, { emit }) {
    console.log("editedRFI ----} ", props.editedRfi);
    const store = useStore();
    const today = new Date().toISOString().split("T")[0];
    const selectedImpacts = ref([]);
    const validationErrors = ref({});
    const currentUser = computed(() => store.state.users.user);
    const userEmail = computed(() => currentUser.value?.email);
    const userLevel = computed(() => currentUser.value?.level || 0);
    const attachments = ref([]);
    const isReassignedStatus = computed(() => {
      return props.editedRfi?.status === "Re-assigned";
    });

    const canEditDirectedTo = computed(() => {
      return props.editMode && isReassignedStatus.value;
    });

    watch(
      () => props.editedRfi?.status,
      (newStatus, oldStatus) => {
        if (newStatus === "Re-assigned" && oldStatus !== "Re-assigned") {
          emit("update-field", { field: "directed_to", value: "" });
          emit("update-field", { field: "directed_to_email", value: "" });
        }
      }
    );

    watch(
      () => props.editedRfi?.possible_impact,
      (newValue) => {
        if (Array.isArray(newValue)) {
          selectedImpacts.value = newValue;
        } else if (newValue) {
          selectedImpacts.value = [newValue];
        } else {
          selectedImpacts.value = [];
        }
      },
      { immediate: true }
    );

    const canReassign = computed(() => {
      return props.isCreateMode
        ? false
        : props.rfi && userEmail.value === props.rfi.directed_to_email;
    });

    const isRequester = computed(() => {
      return props.isCreateMode
        ? false
        : props.rfi && userEmail.value === props.rfi.requestor_email;
    });

    const title = computed(() => {
      if (props.isCreateMode) return "Create New RFI";
      return props.rfi ? `RFI Details - ${props.rfi.ID}` : "RFI Details";
    });

    const canEditDirectedToEmail = computed(() => {
      return (
        props.editMode &&
        isReassignedStatus.value &&
        (isRequester.value || canReassign.value)
      );
    });

    const canEdit = computed(() => {
      if (props.isCreateMode) return false;
      if (userLevel.value === 3) return true;
      if (userLevel.value === 2) return true;
      if (userLevel.value === 1)
        return (
          userEmail.value === props.rfi.requestor_email || canReassign.value
        );
      return false;
    });

    const handleMultiSelectChange = (field) => {
      emit("update-field", {
        field: field,
        value: selectedImpacts.value,
      });
    };

    const IMPACT_FIELD_TRIGGERS = {
      cost_impact: "Cost Impact",
      schedule_impact: "Duration Impact",
      possible_impact_other: "Other",
    };

    const currentImpacts = computed(() => {
      if (props.editMode || props.isCreateMode) {
        return selectedImpacts.value;
      }
      const impact = props.rfi?.possible_impact;
      if (Array.isArray(impact)) return impact;
      return impact ? [impact] : [];
    });

    const shouldShowImpactField = (key) => {
      const trigger = IMPACT_FIELD_TRIGGERS[key];
      if (!trigger) return true;
      return currentImpacts.value.includes(trigger);
    };

    const statusOptions = ref([
      "Submitted and waiting",
      "In Progress",
      "Re-assigned",
      "Completed",
    ]);

    const directedOptions = ref([
      "Forestics",
      "Trekords",
      "Whiteoutwares",
      "Redbit",
      "Topiarytelligence",
      "Soulaid",
      "Cubehive",
      "Goldbooks",
    ]);

    const disciplineOptions = ref(["SPI", "WSH", "WPS", "ITS"]);

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const priorityClasses = (priority) => {
      const priorityMap = {
        High: "bg-red-100 text-red-800",
        Medium: "bg-yellow-100 text-yellow-800",
        Low: "bg-green-100 text-green-800",
      };
      return priorityMap[priority] || "bg-gray-100 text-gray-800";
    };

    const handleFieldUpdate = (field, value) => {
      emit("update-field", { field, value });
    };

    const validateForm = () => {
      validationErrors.value = {};

      if (isReassignedStatus.value) {
        if (!props.editedRfi.directed_to) {
          validationErrors.value.directed_to = "Recipient is required";
        }
        if (!props.editedRfi.directed_to_email) {
          validationErrors.value.directed_to_email =
            "Recipient email is required";
        }
      }

      if (!props.editedRfi.status) {
        validationErrors.value.status = "Status is required";
      }

      if (
        selectedImpacts.value.includes("Other") &&
        !props.editedRfi.possible_impact_other?.trim()
      ) {
        validationErrors.value.possible_impact_other =
          "Please describe the other impact";
      }

      if (props.editedRfi.reassigned_by?.trim()) {
        if (!isValidEmail(props.editedRfi.reassigned_by)) {
          validationErrors.value.reassigned_by =
            "Please enter a valid email address";
        }
      }

      const emailFields = [
        "requestor_email",
        "manager_email",
        "directed_to_email",
      ];

      emailFields.forEach((field) => {
        if (!props.editedRfi[field]?.trim()) {
          validationErrors.value[field] = "Email is required";
        } else if (!isValidEmail(props.editedRfi[field])) {
          validationErrors.value[field] = "Please enter a valid email address";
        }
      });

      return Object.keys(validationErrors.value).length === 0;
    };

    const isValidEmail = (email) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };

    const handleSubmit = () => {
      if (validateForm()) {
        if (props.isCreateMode) {
          emit("create", attachments.value);
        } else {
          emit("save");
        }
      }
    };

    return {
      formatDate,
      priorityClasses,
      canEdit,
      canReassign,
      userLevel,
      userEmail,
      statusOptions,
      directedOptions,
      disciplineOptions,
      handleFieldUpdate,
      canEditDirectedToEmail,
      canEditDirectedTo,
      isReassignedStatus,
      title,
      isRequester,
      handleMultiSelectChange,
      selectedImpacts,
      shouldShowImpactField,
      handleSubmit,
      validationErrors,
      validateForm,
      attachments,
      today,
    };
  },
};
</script>